import { useState } from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import {
  RebalanceItem,
  approveRebalance,
} from 'src/pages/admin/services/xbitRelayRebalancesService';

import { RebalanceSummary } from './RebalanceSummary';
import { WalletSignerPicker, useWalletSigner } from './WalletSigner';
import { buildApproveRebalanceMessage } from './MessageBuilders';
import {
  chainLabel,
  destinationTokenSymbol,
  errorMessage,
  expectedAmountOf,
  formatAmount,
  isTokenSwap,
  isUserRejection,
  sourceTokenSymbol,
} from './helpers';

type ApproveDialogProps = {
  record: RebalanceItem;
  onClose(): void;
};

export const ApproveDialog = ({ record, onClose }: ApproveDialogProps) => {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const signer = useWalletSigner();
  const [submitting, setSubmitting] = useState(false);

  const expectedAmount = expectedAmountOf(record);
  const busy = submitting || signer.connecting;

  const handleSignAndApprove = async () => {
    if (!signer.address || !expectedAmount) return;
    setSubmitting(true);
    try {
      // Stamped fresh at submit time, not at dialog-open time: `timestamp_ms` is inside the
      // signature and wallet-service enforces a freshness window.
      const timestampMs = Date.now();

      const signature = await signer.signMessage(
        buildApproveRebalanceMessage({ id: record.id, timestampMs })
      );

      // snake_case body. `expected_amount` is the stale-approval guard but is NOT part of the
      // signed message — wallet-service checks it separately against the stored row.
      const { data } = await approveRebalance(record.id, {
        admin_id: signer.address.toLowerCase(),
        expected_amount: expectedAmount,
        timestamp_ms: timestampMs,
        signature,
      });

      // Business failures — wrong signer, stale amount, already actioned — arrive as HTTP 200
      // with success: false.
      if (!data?.success) {
        notify(data?.message || t('module.xbitRelayHotWalletBalancer.approveRejected'), {
          type: 'error',
        });
        refresh();
        return;
      }

      notify('module.xbitRelayHotWalletBalancer.approveSuccess', { type: 'success' });
      onClose();
      refresh();
    } catch (error) {
      if (isUserRejection(error)) return;
      notify(errorMessage(error, t('module.xbitRelayHotWalletBalancer.approveRejected')), {
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('module.xbitRelayHotWalletBalancer.approveTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <RebalanceSummary record={record} />

          <Divider />

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('module.xbitRelayHotWalletBalancer.approveConfirm', {
              amount: formatAmount(record.amount),
              token: sourceTokenSymbol(record),
              src: chainLabel(record.srcChainId),
              dst: chainLabel(record.dstChainId),
            })}
          </Typography>

          {isTokenSwap(record) && (
            <Alert severity="warning">
              {t('module.xbitRelayHotWalletBalancer.swapWarning', {
                from: sourceTokenSymbol(record),
                to: destinationTokenSymbol(record),
              })}
            </Alert>
          )}

          <Alert severity="info">
            {t('module.xbitRelayHotWalletBalancer.approveStaleHint')}
          </Alert>

          <Divider />

          <WalletSignerPicker signer={signer} busy={submitting} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={busy}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSignAndApprove}
          disabled={!signer.address || busy || !expectedAmount}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {t('module.xbitRelayHotWalletBalancer.signAndApprove')}
        </Button>
      </DialogActions>
    </>
  );
};
